import { classMap } from 'lit/directives/class-map.js'
import { state, property, query } from 'lit/decorators.js'
import { ref, createRef, Ref } from 'lit/directives/ref.js'
import { html, nothing } from 'lit'

import {
	WithDialog,
	WithFocus,
	WithSwipeDismiss,
	WithLang,
	dialog,
	swipeDismiss,
	compose
} from '#utils/mixins'
import { spring } from '#utils/spring'
import { LfuiElement } from '#utils/element'

import styles from './lfds-sheet.css.ts'

/**
 * A landscape phone can easily exceed the 600px width breakpoint while
 * still being short, so "desktop layout" also requires enough height —
 * otherwise a phone in landscape would incorrectly get the side-sheet
 * layout meant for tablets/desktop. Keep in sync with the equivalent
 * constants in lfds-sheet.css.ts.
 */
const DESKTOP_MEDIA_QUERY = '(min-width: 600px) and (min-height: 600px)'
const MOBILE_MEDIA_QUERY = '(max-width: 599px), (max-height: 599px)'

const translations = {
	sv: {
		close: 'Stäng',
		closePanel: 'Stäng panel',
		cancel: 'Avbryt',
		back: 'Tillbaka'
	},
	en: {
		close: 'Close',
		closePanel: 'Close panel',
		cancel: 'Cancel',
		back: 'Back'
	}
}

/**
 * A sheet overlays content from the side (desktop) or bottom (mobile),
 * providing additional information or actions without pulling users away
 * from their current context.
 *
 * ### Variants
 *
 * - **Content sheet** — Displays read-only information or settings that
 *   take immediate effect (e.g. toggles). Uses the default close button
 *   (✕ Stäng).
 * - **Action sheet** — Contains a form or actions where closing should
 *   discard changes. Set `cancelable` to show "Avbryt" instead of the
 *   close button.
 *
 * ### Accessibility
 *
 * The sheet renders as a native `<dialog>` opened with `showModal()`.
 * Focus moves to the heading when opened. The close button's
 * `aria-label` defaults to _"Stäng panel {heading}"_ (or _"Close panel {heading}"_
 * when `lang="en"`) — override with `sr-close-label` when the heading
 * alone is not descriptive enough.
 *
 * ### Language support
 *
 * Built-in button labels (close, cancel, back) are translated based on
 * the resolved language. Set `lang="en"` on the component or any ancestor
 * element. Supported: `sv` (default), `en`.
 *
 * When using a multi-step flow with `back-button`, provide a byline
 * (e.g. _"1 av 3"_) so screen reader users can track their position
 * in the flow.
 *
 * @see {@link https://lfds.se/components/sheet | Design guidelines}
 *
 * @name LfdsSheet
 *
 * @status ready
 * @tag lfds-sheet
 *
 * @slot default - Main body content.
 * @slot footer - Sticky footer area, typically for primary actions (e.g. `<lfds-button>`).
 *
 * @cssParts sheet - The native `<dialog>` element. Escape hatch — prefer CSS custom properties and design tokens when possible.
 * @cssParts scroll-container - The scrollable body wrapper. Escape hatch — prefer CSS custom properties and design tokens when possible.
 *
 * @cssproperty --lfds--dialog-sheet-background-color
 * @cssproperty --lfds--dialog-sheet-height
 * @cssproperty --lfds--dialog-sheet-width
 */
export default class LfdsSheet extends compose(
	LfuiElement,
	WithDialog,
	WithFocus,
	WithSwipeDismiss,
	WithLang
) {
	static styles = [LfuiElement.styles, styles]
	static events = ['close', 'back']

	// =========================================================================
	// Properties
	// =========================================================================

	/**
	 * Controls whether the sheet is open.
	 */
	@property({ type: Boolean, reflect: true }) open: boolean = false
	/**
	 * Heading of the sheet.
	 */
	@property() heading: string = ''
	/**
	 * Byline under the heading.
	 */
	@property() byline: string = ''
	/**
	 * Horizontal size on larger screens.
	 * @deprecated Desktop will only have one size.
	 * @removal 2026-09-30
	 */
	@property({ type: String }) size: 'small' | 'medium' | 'large' = 'small'
	/**
	 * The height of the sheet on small screens.
	 * @deprecatedValue small - use hug
	 * @removal 2026-09-30
	 * @deprecatedValue medium - use hug or fill
	 * @removal 2026-09-30
	 */
	@property({ type: String }) height: 'small' | 'medium' | 'hug' | 'fill' =
		'hug'
	/**
	 * Allows the user to drag the sheet between `hug` and `fill` snap points on mobile.
	 * When enabled, dragging up expands to fill and dragging down collapses to hug or dismisses.
	 */
	@property({ type: Boolean, reflect: true }) resizable: boolean = true
	/**
	 * If the backdrop should be visible.
	 */
	@property({ type: Boolean }) backdrop: boolean = true
	/**
	 * Enable back button, emits `back` event.
	 */
	@property({ type: Boolean, attribute: 'back-button' }) backButton: boolean =
		false
	/**
	 * Will change close button to a cancel button.
	 */
	@property({ type: Boolean }) cancelable: boolean = false
	/**
	 * Accessible name for close button (`aria-label`).
	 */
	@property({ type: String, attribute: 'sr-close-label' }) srCloseLabel = ''
	/**
	 * Accessible name for cancel button (`aria-label`).
	 */
	@property({ type: String, attribute: 'sr-cancel-label' }) srCancelLabel = ''
	/**
	 * Accessible name for back button (`aria-label`).
	 */
	@property({ type: String, attribute: 'sr-back-label' }) srBackLabel = ''
	/**
	 * Opt-in to upcoming breaking changes. Space-separated list of migration names.
	 * Each migration represents a specific change that can be adopted independently.
	 *
	 * Available migrations:
	 * - `light-background` — Uses a light background color instead of the default grey.
	 * - `remove-padding` — Removes inline padding from the body content area.
	 *
	 * Migrate before removal date. After this date the new behaviour becomes the default and the
	 * migration attribute will be a no-op.
	 *
	 * @deprecatedValue remove-padding
	 * @removal 2026-09-30
	 * @deprecatedValue light-background
	 * @removal 2026-09-30
	 */
	@property({ type: String, reflect: true })
	migration:
		| 'light-background'
		| 'remove-padding'
		| 'light-background remove-padding'
		| (string & {}) = ''

	// =========================================================================
	// Queries
	// =========================================================================

	@query('.header-wrapper') private headerEl!: HTMLElement
	@query('.inner') private innerEl!: HTMLElement
	@query('.body') private bodyEl!: HTMLElement
	@query('dialog') private dialogEl!: HTMLElement

	// =========================================================================
	// Internal state
	// =========================================================================

	@state() _open = false
	@state() _hidden = true
	@state() _snap: 'hug' | 'fill' = 'hug'
	@state() _resizing = false
	@state() _sticky = false

	headingRef: Ref<HTMLHeadingElement> = createRef()

	// =========================================================================
	// Private fields
	// =========================================================================

	#observer: ResizeObserver | null = null
	#contentObserver: ResizeObserver | null = null
	#resizeTimeout: ReturnType<typeof setTimeout> | null = null
	#contentResizeTimeout: ReturnType<typeof setTimeout> | null = null
	#spacingCache: number | null = null
	#springRafId: number | null = null
	#mediaQuery: MediaQueryList | null = null
	#dragStartY = 0
	#dragStartHeight = 0
	#dragVelocity = 0
	#dragLastY = 0
	#dragLastTime = 0

	// =========================================================================
	// Mixin config
	// =========================================================================

	get [swipeDismiss]() {
		return {
			target: this.dialogEl,
			gestures: [
				{
					direction: 'swipedown' as const,
					handle: this.innerEl,
					scrollContainer: this.bodyEl,
					media: MOBILE_MEDIA_QUERY,
					disabled: this.resizable
				}
			]
		}
	}

	// =========================================================================
	// Lifecycle
	// =========================================================================

	connectedCallback() {
		super.connectedCallback()
		if (!this.#observer) {
			this.#observer = new ResizeObserver(this.#onResize)
		}
		if (!this.#contentObserver) {
			this.#contentObserver = new ResizeObserver(this.#onContentResize)
		}
		this.#mediaQuery = window.matchMedia(DESKTOP_MEDIA_QUERY)
		this.#mediaQuery.addEventListener('change', this.#onBreakpointChange)
	}

	disconnectedCallback() {
		super.disconnectedCallback()
		this.#observer?.disconnect()
		this.#contentObserver?.disconnect()
		clearTimeout(this.#contentResizeTimeout)
		this.#mediaQuery?.removeEventListener('change', this.#onBreakpointChange)
		this.#teardownResizeDrag()
	}

	firstUpdated(changed: Map<string, unknown>) {
		super.firstUpdated?.(changed)
		if (this.resizable) {
			this.#setupResizeDrag()
		}
		// Observe slotted content for size changes
		const slot = this.bodyEl?.querySelector(
			'slot:not([name])'
		) as HTMLSlotElement
		if (slot) {
			slot.addEventListener('slotchange', this.#onSlotChange)
			this.#observeSlottedContent(slot)
		}
	}

	updated(changedProperties: Map<string, any>) {
		super.updated?.(changedProperties)
		if (changedProperties.has('open') && this.open !== this._open) {
			this.#toggle(this.open)
		}
		if (changedProperties.has('resizable')) {
			if (this.resizable) {
				this.#setupResizeDrag()
			} else {
				this.#teardownResizeDrag()
			}
		}
	}

	// =========================================================================
	// Public API
	// =========================================================================

	showModal() {
		this.#toggle(true)
	}

	close(value?: string) {
		this.#toggle(false, value)
	}

	get returnValue() {
		return this[dialog].value?.returnValue
	}

	// =========================================================================
	// Open / Close logic
	// =========================================================================

	#toggle(open: boolean = !this.open, value?: string) {
		if (open) {
			this.open = true
			this._hidden = false
			this._snap = this.height === 'fill' ? 'fill' : 'hug'
			this[dialog].value?.showModal()

			// Reset any inline height from previous drag session
			this[dialog].value?.style.removeProperty('height')
			this[dialog].value?.style.removeProperty('max-height')
			this[dialog].value?.style.removeProperty('transition')

			const isMobile = !window.matchMedia(DESKTOP_MEDIA_QUERY).matches
			const needsMeasurement =
				isMobile && (this.height === 'hug' || this.resizable)

			if (needsMeasurement) {
				// Measure height BEFORE triggering open animation to avoid jump.
				// Dialog is already in top layer (showModal) but off-screen (translateY(100%)).
				requestAnimationFrame(() => {
					if (!this.open) return
					const fullHeight = this.#getFullHeight()
					const h = this._snap === 'fill' ? fullHeight : this.#getHugHeight()
					// If hug fills all available space, upgrade to fill so content can scroll
					if (this._snap === 'hug' && h >= fullHeight) {
						this._snap = 'fill'
					}
					// Suppress height transition during initial open
					this.dialogEl.style.transition = 'none'
					this.dialogEl.style.height = `${h}px`
					this.dialogEl.style.maxHeight = 'none'
					// Force style recalc so height is committed without transition
					void this.dialogEl.offsetHeight
					this.dialogEl.style.removeProperty('transition')
					// Now trigger the slide-in animation
					this._open = true
					setTimeout(
						() => (this.headingRef as Ref<HTMLElement>).value?.focus(),
						0
					)
				})
			} else {
				this._open = true
				setTimeout(
					() => (this.headingRef as Ref<HTMLElement>).value?.focus(),
					0
				)
			}
		} else {
			this._open = false
			const dialogEl = this[dialog].value
			const hasTransition =
				getComputedStyle(dialogEl).transitionDuration !== '0s'

			if (hasTransition) {
				dialogEl?.addEventListener(
					'transitionend',
					() => {
						dialogEl?.close(value)
						dialogEl?.style.removeProperty('height')
						dialogEl?.style.removeProperty('max-height')
						dialogEl?.style.removeProperty('transition')
					},
					{ once: true }
				)
			} else {
				dialogEl?.close(value)
				dialogEl?.style.removeProperty('height')
				dialogEl?.style.removeProperty('max-height')
				dialogEl?.style.removeProperty('transition')
			}
		}
	}

	// =========================================================================
	// Height measurement
	// =========================================================================

	#getHugHeight(): number {
		if (!this.innerEl || !this.bodyEl) return window.innerHeight * 0.5

		const header = this.innerEl.querySelector('.header-wrapper') as HTMLElement
		const actions = this.innerEl.querySelector('.header-actions') as HTMLElement
		const footer = this.innerEl.querySelector('.footer') as HTMLElement

		// Header row: max of header-wrapper and actions (they overlap in same grid row)
		const headerH = Math.max(
			header?.offsetHeight ?? 0,
			actions?.offsetHeight ?? 0
		)

		// Body: temporarily shrink to 0 so scrollHeight reports full content height
		const prevBodyH = this.bodyEl.style.height
		this.bodyEl.style.height = '0px'
		const bodyH = this.bodyEl.scrollHeight
		this.bodyEl.style.height = prevBodyH

		const footerH = footer?.offsetHeight ?? 0
		const natural = headerH + bodyH + footerH
		return Math.min(natural, this.#getFullHeight())
	}

	#getFullHeight(): number {
		if (this.#spacingCache === null) {
			this.#spacingCache =
				parseFloat(
					getComputedStyle(this.dialogEl).getPropertyValue(
						'--lfds--spacing-3xl'
					)
				) || 32
		}
		return window.innerHeight - this.#spacingCache * 2
	}

	#getMinHeight(): number {
		const header = this.innerEl?.querySelector('.header-wrapper') as HTMLElement
		const actions = this.innerEl?.querySelector(
			'.header-actions'
		) as HTMLElement
		return Math.max(header?.offsetHeight ?? 0, actions?.offsetHeight ?? 0, 100)
	}

	// =========================================================================
	// Drag-to-resize (mobile only, when resizable)
	// =========================================================================

	#setupResizeDrag() {
		if (!this.headerEl) return
		// Only active on mobile — desktop sheet is always full height
		if (this.#mediaQuery?.matches) return
		this.headerEl.addEventListener('touchstart', this.#onDragStart, {
			passive: true
		})
		this.bodyEl?.addEventListener('touchstart', this.#onBodyDragStart, {
			passive: true
		})
	}

	#teardownResizeDrag() {
		if (!this.headerEl) return
		this.headerEl.removeEventListener('touchstart', this.#onDragStart)
		this.bodyEl?.removeEventListener('touchstart', this.#onBodyDragStart)
		this.#cleanupDragListeners()
	}

	#dragHandle: HTMLElement | null = null
	#dragEngaged: boolean | undefined = undefined

	#cleanupDragListeners() {
		if (this.#dragHandle) {
			this.#dragHandle.removeEventListener('touchmove', this.#onDragMove)
			this.#dragHandle.removeEventListener('touchend', this.#onDragEnd)
			this.#dragHandle.removeEventListener('touchcancel', this.#onDragEnd)
			this.#dragHandle = null
		}
	}

	#onDragStart = (e: TouchEvent) => {
		if (!this.resizable || !this._open) return
		this.#startDrag(e, this.headerEl, true)
	}

	#onBodyDragStart = (e: TouchEvent) => {
		if (!this.resizable || !this._open) return
		// Bail if touch originated inside a nested scrollable element
		if (this.#isInsideNestedScroller(e.target as HTMLElement)) return
		// At hug snap: body drag always resizes (expand first, then scroll)
		// At fill snap: only engage if scrolled to top (collapse/dismiss)
		if (this._snap === 'fill' && this.bodyEl.scrollTop > 1) return
		this.#startDrag(e, this.bodyEl, this._snap === 'hug')
	}

	#isInsideNestedScroller(target: HTMLElement | null): boolean {
		let el = target
		while (el && el !== this.bodyEl) {
			const style = getComputedStyle(el)
			const overflowY = style.overflowY
			if (
				(overflowY === 'auto' || overflowY === 'scroll') &&
				el.scrollHeight > el.clientHeight
			) {
				return true
			}
			el = el.parentElement
		}
		return false
	}

	#startDrag(e: TouchEvent, handle: HTMLElement, immediate: boolean) {
		if (this.#springRafId) {
			cancelAnimationFrame(this.#springRafId)
			this.#springRafId = null
		}

		this.#dragStartY = e.touches[0].clientY
		this.#dragStartHeight = this.dialogEl.getBoundingClientRect().height
		this.#dragVelocity = 0
		this.#dragLastY = this.#dragStartY
		this.#dragLastTime = performance.now()
		this.#dragHandle = handle
		this.#dragEngaged = immediate ? true : undefined

		if (immediate) {
			this.dialogEl.style.transition = 'none'
		}

		handle.addEventListener('touchmove', this.#onDragMove, {
			passive: false
		})
		handle.addEventListener('touchend', this.#onDragEnd)
		handle.addEventListener('touchcancel', this.#onDragEnd)
	}

	#onDragMove = (e: TouchEvent) => {
		const currentY = e.touches[0].clientY
		const deltaY = this.#dragStartY - currentY
		const now = performance.now()
		const dt = now - this.#dragLastTime

		if (dt > 0) {
			this.#dragVelocity = (this.#dragLastY - currentY) / dt
		}
		this.#dragLastY = currentY
		this.#dragLastTime = now

		// Deferred engagement for body at fill: only engage on downward drag
		if (this.#dragEngaged === undefined) {
			if (deltaY > 0) {
				// Dragging up — let native scroll handle it
				this.#dragEngaged = false
				return
			}
			if (deltaY < -5) {
				// Dragging down past threshold — engage resize
				this.#dragEngaged = true
				this.dialogEl.style.transition = 'none'
			}
			return
		}

		if (!this.#dragEngaged) return

		e.preventDefault()

		const fullHeight = this.#getFullHeight()
		const minHeight = this.#getMinHeight()
		const newHeight = Math.max(
			minHeight,
			Math.min(fullHeight, this.#dragStartHeight + deltaY)
		)

		this.dialogEl.style.height = `${newHeight}px`
		this.dialogEl.style.maxHeight = 'none'
	}

	#onDragEnd = () => {
		this.#cleanupDragListeners()

		if (!this.#dragEngaged) {
			this.#dragEngaged = undefined
			return
		}
		this.#dragEngaged = undefined

		const currentHeight = this.dialogEl.getBoundingClientRect().height
		const hugHeight = this.#getHugHeight()
		const fullHeight = this.#getFullHeight()
		const velocity = this.#dragVelocity
		const fastSwipe = Math.abs(velocity) > 0.5

		if (fastSwipe && velocity < -0.5) {
			if (this._snap === 'hug' || currentHeight < hugHeight * 0.7) {
				this.#animateToAndDismiss()
				return
			} else {
				this._snap = 'hug'
			}
		} else if (fastSwipe && velocity > 0.5) {
			this._snap = 'fill'
		} else {
			const midPoint = (hugHeight + fullHeight) / 2
			if (currentHeight < hugHeight * 0.7) {
				this.#animateToAndDismiss()
				return
			} else if (currentHeight < midPoint) {
				this._snap = 'hug'
			} else {
				this._snap = 'fill'
			}
		}

		// If hug fills all available space, upgrade to fill so content can scroll
		if (this._snap === 'hug' && hugHeight >= fullHeight) {
			this._snap = 'fill'
		}

		const targetHeight = this._snap === 'fill' ? fullHeight : hugHeight
		this.#animateToHeight(targetHeight)
	}

	// =========================================================================
	// Animations
	// =========================================================================

	#animateToHeight(height: number) {
		if (this.#springRafId) {
			cancelAnimationFrame(this.#springRafId)
			this.#springRafId = null
		}

		const current = this.dialogEl.getBoundingClientRect().height
		if (Math.abs(current - height) < 1) {
			this.dialogEl.style.height = `${height}px`
			return
		}

		// Respect reduced motion: snap instantly
		if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
			this.dialogEl.style.transition = 'none'
			this.dialogEl.style.height = `${height}px`
			return
		}

		let value = current
		let prevValue = current

		const animate = () => {
			const state = spring(value, height, prevValue, 1 / 60, 1, 2.5, 0.35)
			prevValue = value
			value = state.value
			this.dialogEl.style.height = `${value}px`

			if (!state.settled) {
				this.#springRafId = requestAnimationFrame(animate)
			} else {
				this.dialogEl.style.height = `${height}px`
				this.#springRafId = null
			}
		}

		this.dialogEl.style.transition = 'none'
		this.#springRafId = requestAnimationFrame(animate)
	}

	#animateToAndDismiss() {
		// Respect reduced motion: dismiss instantly
		if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
			this.close()
			return
		}

		const currentHeight = this.dialogEl.getBoundingClientRect().height
		const targetDist = currentHeight
		const velocity = Math.abs(this.#dragVelocity)
		const duration = Math.round(
			Math.max(80, Math.min(300, targetDist / Math.max(velocity, 0.5)))
		)

		let dismissed = false
		const cleanup = () => {
			if (dismissed) return
			dismissed = true
			this.dialogEl.style.removeProperty('transform')
			this.dialogEl.style.removeProperty('transition')
			this.close()
		}

		this.dialogEl.style.transition = `transform ${duration}ms cubic-bezier(0.4, 0, 1, 1)`
		this.dialogEl.style.transform = `translateY(${targetDist}px)`

		this.dialogEl.addEventListener('transitionend', cleanup, { once: true })
		setTimeout(cleanup, duration + 100)
	}

	// =========================================================================
	// Event handlers
	// =========================================================================

	#onBreakpointChange = (e: MediaQueryListEvent) => {
		if (!this._open) return
		if (e.matches) {
			// Crossed to desktop — remove inline height so CSS takes over
			this.dialogEl.style.removeProperty('height')
			this.dialogEl.style.removeProperty('max-height')
			// Tear down mobile-only resize drag
			if (this.resizable) this.#teardownResizeDrag()
		} else {
			// Crossed to mobile — re-measure hug height
			if (this.height === 'hug' || this.resizable) {
				const h =
					this._snap === 'fill' ? this.#getFullHeight() : this.#getHugHeight()
				this.dialogEl.style.transition = 'none'
				this.dialogEl.style.height = `${h}px`
				this.dialogEl.style.maxHeight = 'none'
				void this.dialogEl.offsetHeight
				this.dialogEl.style.removeProperty('transition')
			}
			// Re-setup resize drag for mobile
			if (this.resizable) this.#setupResizeDrag()
		}
	}

	#onResize = () => {
		this._resizing = true
		clearTimeout(this.#resizeTimeout)
		this.#resizeTimeout = setTimeout(() => {
			this._resizing = false
			this.#recalculateHeight()
		}, 100)
	}

	#onContentResize = () => {
		if (!this._open) return
		// Don't recalculate during active drag
		if (this.#dragEngaged) return
		// Debounce to batch rapid changes (e.g. multiple elements resizing)
		clearTimeout(this.#contentResizeTimeout)
		this.#contentResizeTimeout = setTimeout(() => {
			this.#recalculateHeight()
		}, 16)
	}

	#onSlotChange = (e: Event) => {
		const slot = e.target as HTMLSlotElement
		this.#observeSlottedContent(slot)
		if (this._open) {
			this.#recalculateHeight()
		}
	}

	#observeSlottedContent(slot: HTMLSlotElement) {
		this.#contentObserver?.disconnect()
		const elements = slot.assignedElements()
		for (const el of elements) {
			this.#contentObserver?.observe(el)
		}
	}

	#recalculateHeight() {
		if (!this._open) return
		// Only applies on mobile
		if (this.#mediaQuery?.matches) return
		// Only applies to hug/resizable sheets
		if (!this.resizable && this.height !== 'hug') return
		// Don't recalculate during spring animation
		if (this.#springRafId) return

		// Invalidate spacing cache on resize
		this.#spacingCache = null

		const fullHeight = this.#getFullHeight()
		const targetHeight =
			this._snap === 'fill' ? fullHeight : this.#getHugHeight()

		// If hug fills all available space, upgrade to fill so content can scroll
		if (this._snap === 'hug' && targetHeight >= fullHeight) {
			this._snap = 'fill'
		}

		this.dialogEl.style.transition = 'none'
		this.dialogEl.style.height = `${targetHeight}px`
		this.dialogEl.style.maxHeight = 'none'
		void this.dialogEl.offsetHeight
		this.dialogEl.style.removeProperty('transition')
	}

	#onScroll(e: Event) {
		this._sticky = (e.currentTarget as HTMLElement).scrollTop > 1
	}

	#onCancel = (event: Event) => {
		event.preventDefault()
		this.close()
	}

	#onBack = () => {
		this.dispatchEvent(
			new CustomEvent('back', { bubbles: true, composed: true })
		)
	}

	#onClose = () => {
		this._hidden = true
		this.open = false
		this.dispatchEvent(
			new CustomEvent('close', { bubbles: true, composed: true })
		)
	}

	#onObserve = (el: Element | undefined) => {
		if (el) {
			this.#observer?.observe(el)
		}
	}

	// =========================================================================
	// Render
	// =========================================================================

	headerActionsTemplate() {
		return html`<div class="header-actions">
			${
				this.backButton
					? html`
							<button
								type="button"
								@click=${this.#onBack}
								aria-label="${this.srBackLabel || this.t(translations, 'back')}"
								class=${classMap({
									'header-action header-action--start': true,
									'focus-visible': this.focusVisible
								})}
							>
								<span class="header-action__label">
									<lfds-icon
										icon-id="chevron-left"
										sizes="24"
										class="header-action__label-icon"
									></lfds-icon>
									<span class="header-action__label-text"
										>${this.t(translations, 'back')}</span
									>
								</span>
							</button>
						`
					: nothing
			}
			<button
				type="button"
				@click=${() => this.close()}
				aria-label="${
					this.cancelable
						? this.srCancelLabel || this.t(translations, 'cancel')
						: this.srCloseLabel ||
							`${this.t(translations, 'closePanel')} ${this.heading}`
				}"
				class=${classMap({
					'header-action header-action--end': true,
					'header-action--text-only': this.cancelable,
					'focus-visible': this.focusVisible
				})}
			>
				<span class="header-action__label">
					${
						this.cancelable
							? html`<span class="header-action__label-text"
									>${this.t(translations, 'cancel')}</span
								>`
							: html`<span class="header-action__label-text"
										>${this.t(translations, 'close')}</span
									>
									<lfds-icon
										icon-id="cross-big"
										sizes="24"
										class="header-action__label-icon"
									></lfds-icon>`
					}
				</span>
			</button>
		</div>`
	}

	render() {
		const effectiveHeight = this.resizable ? undefined : this.height
		return html`
			<dialog
				${ref(this[dialog])}
				closedby="any"
				aria-labelledby="sheet-heading"
				class=${classMap({
					dialog: true,
					backdrop: this.backdrop,
					open: this._open,
					hidden: this._hidden,
					resizing: this._resizing,
					resizable: this.resizable,
					[`size-${this.size}`]: !!this.size,
					[`height-${effectiveHeight}`]: !!effectiveHeight
				})}
				@cancel=${this.#onCancel}
				@close=${this.#onClose}
				part="sheet"
			>
				<div
					class=${classMap({ inner: true, sticky: this._sticky })}
					part="scroll-container"
				>
					<div class="header-wrapper">
						<h2
							id="sheet-heading"
							${ref(this.headingRef)}
							tabindex="-1"
							class=${classMap({
								heading: true,
								'focus-visible': this.focusVisible
							})}
						>
							${this.heading}
							${
								this.byline
									? html`<span class="sr-only">,</span>
											<div class="byline">${this.byline}</div>`
									: nothing
							}
						</h2>
					</div>
					<div
						class=${classMap({
							body: true,
							'snap-hug': this.resizable && this._snap === 'hug'
						})}
						@scroll=${this.#onScroll}
					>
						<slot></slot>
					</div>
					<div class="footer">
						<slot name="footer"></slot>
					</div>
					${this.headerActionsTemplate()}
				</div>
				<div class="observable" ${ref(this.#onObserve)}></div>
			</dialog>
		`
	}
}
